import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useClinica } from '../../context/ClinicaContext';

import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    Payments as CashIcon,
    CreditCard as CardIcon,
    AccountBalance as BankIcon
} from '@mui/icons-material';
import { euros } from '../../utils/formatos';

// 🎯 MEJORA: Añadido el prop 'paciente' para apertura directa desde el Archivo Clínico
const ModalNuevaFactura = ({ open, onClose, onSuccess, citaPrevia, paciente, provisional = false }) => {
    const { user } = useAuth();
    // 🏥 Tarifario de la clínica: líneas rápidas y precio/IVA de la cita
    const { servicios, termino } = useClinica();
    const conceptoPorDefecto = () => ({ descripcion: termino('consulta'), precio: 50, iva: 0 });
    // Propuesta del servidor para la cita: servicio del tarifario, bono o ya facturada
    const [propuesta, setPropuesta] = useState(null);

    const [pacientes, setPacientes] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);

    const [loadingPacientes, setLoadingPacientes] = useState(false);
    const [loading, setLoading] = useState(false);
    const [metodoPago, setMetodoPago] = useState('tarjeta');
    const [observaciones, setObservaciones] = useState('');

    const [conceptos, setConceptos] = useState(() => [conceptoPorDefecto()]);
    const dropdownRef = useRef(null);

    // 🧾 Tipo de factura: F2 = simplificada (ticket habitual, sin datos del
    // cliente); F1 = completa, cuando el paciente pide factura a su nombre/NIF
    // (p. ej. para su seguro o para deducir). F1 exige identificar al destinatario.
    const [tipoFactura, setTipoFactura] = useState('F2');
    const [destinatario, setDestinatario] = useState({ nif: '', nombre: '', domicilio: '' });
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (open) {
            setErrorMsg('');
            setTipoFactura('F2');
            setDestinatario({ nif: '', nombre: '', domicilio: '' });
            setPropuesta(null);
            if (citaPrevia) {
                setPacienteSeleccionado(citaPrevia.paciente || null);
                if (citaPrevia.precioCobrado) {
                    setConceptos([{ descripcion: citaPrevia.motivo || 'Consulta Médica', precio: Number(citaPrevia.precioCobrado) }]);
                }
            } else if (paciente) {
                // 🎯 Vinculación directa si se abre desde el listado de pacientes
                setPacienteSeleccionado(paciente);
            } else {
                setPacienteSeleccionado(null);
                setBusqueda('');
                setConceptos([conceptoPorDefecto()]);
                setObservaciones('');
                setMetodoPago('tarjeta');
                setPacientes([]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, citaPrevia, paciente]);

    // 🧾 Cobro de una cita: el servidor propone el servicio del tarifario con
    // su precio e IVA, o avisa si la cita va con bono o ya está facturada.
    useEffect(() => {
        const citaId = citaPrevia?.id || citaPrevia?._id;
        if (!open || !citaId) return undefined;
        let vigente = true;
        api.get(`/facturas/propuesta/${citaId}`)
            .then(res => {
                if (!vigente) return;
                setPropuesta(res);
                if (res.conceptos?.length) setConceptos(res.conceptos.map(c => ({ ...c })));
                else if (res.cubiertaPorBono || res.cubiertaPorSeguro) setConceptos([{ descripcion: '', precio: 0, iva: 0 }]);
            })
            .catch(() => { /* sin propuesta: se cobra a mano como siempre */ });
        return () => { vigente = false; };
    }, [open, citaPrevia]);

    useEffect(() => {
        if (!busqueda || busqueda.trim().length < 2 || pacienteSeleccionado) {
            setPacientes([]);
            return;
        }

        const retrasarBusqueda = setTimeout(async () => {
            try {
                setLoadingPacientes(true);
                const res = await api.get(`/pacientes?search=${encodeURIComponent(busqueda)}&limit=5`);
                const listadoPacientes = Array.isArray(res) ? res : (res?.data || res?.docs || []);
                setPacientes(listadoPacientes);
            } catch (err) {
                console.error("Error en autocompletado de pacientes:", err);
                setPacientes([]);
            } finally {
                setLoadingPacientes(false);
            }
        }, 350);

        return () => clearTimeout(retrasarBusqueda);
    }, [busqueda, pacienteSeleccionado]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleAgregarConcepto = () => {
        setConceptos([...conceptos, { descripcion: '', precio: 0, iva: 0 }]);
    };

    // Línea rápida desde el tarifario (sustituye la primera si está vacía)
    const anadirDelTarifario = (codigo) => {
        const s = servicios.find(x => x.codigo === codigo);
        if (!s) return;
        const linea = { descripcion: s.nombre, precio: s.precio, iva: s.iva || 0 };
        setConceptos(lista => (lista.length === 1 && !lista[0].descripcion.trim() && !lista[0].precio) ? [linea] : [...lista, linea]);
    };

    const handleEliminarConcepto = (index) => {
        if (conceptos.length === 1) return;
        setConceptos(conceptos.filter((_, i) => i !== index));
    };

    const handleConceptoChange = (index, campo, valor) => {
        const nuevosConceptos = [...conceptos];
        nuevosConceptos[index] = { ...nuevosConceptos[index], [campo]: (campo === 'precio' || campo === 'iva') ? Number(valor) : valor };
        setConceptos(nuevosConceptos);
    };

    const totalFactura = Math.round(conceptos.reduce((sum, item) => sum + (item.precio || 0), 0) * 100) / 100;
    // IVA incluido en los precios (solo informativo; el desglose lo hace el servidor)
    const ivaIncluido = Math.round(conceptos.reduce((sum, c) => {
        const tipo = Number(c.iva) || 0;
        return sum + (tipo ? (c.precio || 0) - (c.precio || 0) / (1 + tipo / 100) : 0);
    }, 0) * 100) / 100;

    const pacienteActual = citaPrevia?.paciente || pacienteSeleccionado || paciente || null;

    // Al pasar a F1 rellenamos el destinatario con los datos del paciente
    // (editable: puede facturarse a otra persona o a una empresa).
    const cambiarTipo = (tipo) => {
        setTipoFactura(tipo);
        setErrorMsg('');
        if (tipo === 'F1' && pacienteActual && !destinatario.nif && !destinatario.nombre) {
            setDestinatario({
                nif: (pacienteActual.dni || '').toUpperCase(),
                nombre: `${pacienteActual.nombre || ''} ${pacienteActual.apellidos || ''}`.trim(),
                domicilio: [pacienteActual.direccion, pacienteActual.codigoPostal, pacienteActual.ciudad].filter(Boolean).join(', ')
            });
        }
    };

    const handleSubmit = async () => {
        const pacienteId = citaPrevia?.paciente?.id || citaPrevia?.paciente?._id || pacienteSeleccionado?.id || pacienteSeleccionado?._id;
        const profesionalId = user?.id || user?._id;

        setErrorMsg('');
        if (!pacienteId) {
            setErrorMsg('Selecciona el paciente al que se cobra.');
            return;
        }

        if (!profesionalId) {
            setErrorMsg('Error de sesión: vuelve a iniciar sesión.');
            return;
        }

        const conceptosInvalidos = conceptos.some(c => !c.descripcion.trim() || !(c.precio > 0));
        if (conceptosInvalidos) {
            setErrorMsg('Cada línea necesita una descripción y un precio mayor que cero.');
            return;
        }

        if (tipoFactura === 'F1') {
            if (!/^[A-Z0-9]{9}$/i.test(destinatario.nif.replace(/[\s-]/g, ''))) {
                setErrorMsg('Para una factura completa indica el NIF/DNI del destinatario (9 caracteres).');
                return;
            }
            if (destinatario.nombre.trim().length < 2) {
                setErrorMsg('Para una factura completa indica el nombre o razón social del destinatario.');
                return;
            }
        }

        setLoading(true);
        try {
            const payload = {
                paciente: pacienteId,
                consulta: citaPrevia?.id || citaPrevia?._id || undefined,
                conceptos: conceptos.map(c => ({ descripcion: c.descripcion.trim(), precio: Number(c.precio), iva: Number(c.iva) || 0 })),
                total: totalFactura,
                metodoPago: metodoPago,
                observaciones: observaciones,
                estado: 'pagada',
                tipoFactura,
                ...(tipoFactura === 'F1' && {
                    destinatario: {
                        nif: destinatario.nif.replace(/[\s-]/g, '').toUpperCase(),
                        nombre: destinatario.nombre.trim(),
                        ...(destinatario.domicilio.trim() && { domicilio: destinatario.domicilio.trim() })
                    }
                })
            };

            await api.post('/facturas', payload);

            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            setErrorMsg(error.message || 'No se pudo emitir la factura.');
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
            <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

                <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50">
                    <h2 className="text-xl font-black text-slate-900 tracking-tight">
                        {provisional ? 'Nuevo documento provisional' : 'Generar Recibo de Caja / Factura'}
                    </h2>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors focus:outline-none">
                        <CloseIcon />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {provisional && (
                        <p className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-900">
                            Plan Esencial: este documento no es una factura ni se envía a Hacienda. Sirve como justificante para el paciente.
                        </p>
                    )}
                    {propuesta?.yaFacturada && (
                        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm font-semibold text-amber-800" role="status">
                            Esta cita ya tiene una factura emitida. Puedes verla en Facturas y Caja.
                        </div>
                    )}
                    {propuesta?.cubiertaPorBono && !propuesta?.yaFacturada && (
                        <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm font-semibold text-emerald-800" role="status">
                            Esta cita está pagada con el <strong>{propuesta.cubiertaPorBono.nombre}</strong>. Solo hace falta cobrar si hay algún extra.
                        </div>
                    )}
                    {propuesta?.cubiertaPorSeguro && !propuesta?.yaFacturada && (
                        <div className="p-3 rounded-xl bg-sky-50 border border-sky-200 text-sm font-semibold text-sky-900 space-y-1" role="status">
                            <p>
                                Cita por seguro: <strong>{propuesta.cubiertaPorSeguro.aseguradora}</strong> paga {euros(Number(propuesta.cubiertaPorSeguro.importe))} y se le factura a ella.
                                {propuesta.cubiertaPorSeguro.copago > 0
                                    ? <> Al paciente solo se le cobra el copago de <strong>{euros(Number(propuesta.cubiertaPorSeguro.copago))}</strong>.</>
                                    : ' Al paciente no se le cobra nada, salvo algún extra.'}
                            </p>
                            {propuesta.cubiertaPorSeguro.faltaAutorizacion && (
                                <p className="text-amber-700 font-bold">Falta el número de autorización: sin él la aseguradora puede rechazar el pago.</p>
                            )}
                        </div>
                    )}
                    <div>
                        <label htmlFor="search-paciente-factura" className="block mb-1.5 text-xs font-bold text-slate-500 uppercase tracking-wide">
                            Paciente Asociado
                        </label>
                        {/* 🎯 MEJORA: Banner polimórfico bloqueado si el paciente viene inyectado de antemano */}
                        {citaPrevia || paciente ? (
                            <div className="w-full py-3 px-4 bg-slate-100 border border-slate-200 rounded-xl text-slate-700 font-bold flex justify-between items-center cursor-not-allowed">
                                <span>
                                    {(citaPrevia?.paciente || paciente)?.nombre || ''} {(citaPrevia?.paciente || paciente)?.apellidos || ''}
                                </span>
                                <span className="text-[10px] font-black uppercase tracking-widest text-blue-600 bg-blue-100 px-2.5 py-1 rounded-lg">
                                    {citaPrevia ? 'Cita Vinculada' : 'Ficha Activa'}
                                </span>
                            </div>
                        ) : (
                            <div className="relative" ref={dropdownRef}>
                                <div className="relative">
                                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" fontSize="small" />
                                    <input
                                        id="search-paciente-factura"
                                        type="text"
                                        placeholder="Buscar expediente..."
                                        value={pacienteSeleccionado ? `${pacienteSeleccionado.nombre} ${pacienteSeleccionado.apellidos} - ${pacienteSeleccionado.dni}` : busqueda}
                                        onChange={(e) => {
                                            setPacienteSeleccionado(null);
                                            setBusqueda(e.target.value);
                                            setShowDropdown(true);
                                        }}
                                        onFocus={() => setShowDropdown(true)}
                                        className="w-full py-3 pl-10 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium focus:outline-none focus:border-clinica-primary focus:ring-1 focus:ring-clinica-primary transition-all"
                                    />
                                    {loadingPacientes && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            <div className="w-4 h-4 border-2 border-clinica-primary border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    )}
                                </div>

                                {showDropdown && busqueda.length >= 2 && !pacienteSeleccionado && (
                                    <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                                        {pacientes.length === 0 && !loadingPacientes ? (
                                            <div className="p-4 text-center text-sm font-bold text-slate-500">No se encontraron pacientes.</div>
                                        ) : (
                                            pacientes.map(p => (
                                                <button
                                                    key={p.id || p._id}
                                                    type="button"
                                                    onClick={() => {
                                                        setPacienteSeleccionado(p);
                                                        setBusqueda('');
                                                        setShowDropdown(false);
                                                    }}
                                                    className="w-full flex flex-col items-start px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors focus:outline-none focus:bg-slate-50"
                                                >
                                                    <span className="font-bold text-slate-900">{p.nombre} {p.apellidos}</span>
                                                    <span className="text-xs font-semibold text-slate-500">DNI: {p.dni}</span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div role="group" aria-label="Tipo de factura">
                        <div className="block mb-2 text-xs font-bold text-slate-500 uppercase tracking-wide">Tipo de factura</div>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => cambiarTipo('F2')}
                                className={`text-left p-3 rounded-xl border-2 transition-all focus:outline-none ${tipoFactura === 'F2' ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                            >
                                <span className="block text-sm font-black text-slate-900">Simplificada (ticket)</span>
                                <span className="block text-xs font-semibold text-slate-500">Lo habitual. Sin datos fiscales del cliente.</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => cambiarTipo('F1')}
                                className={`text-left p-3 rounded-xl border-2 transition-all focus:outline-none ${tipoFactura === 'F1' ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                            >
                                <span className="block text-sm font-black text-slate-900">Completa (con NIF)</span>
                                <span className="block text-xs font-semibold text-slate-500">Cuando la pide el paciente (seguro, deducción…).</span>
                            </button>
                        </div>
                    </div>

                    {tipoFactura === 'F1' && (
                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide">Datos del destinatario</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <label htmlFor="dest-nif" className="block mb-1 text-[11px] font-bold text-slate-500">NIF / DNI</label>
                                    <input id="dest-nif" type="text" value={destinatario.nif} onChange={(e) => setDestinatario(d => ({ ...d, nif: e.target.value.toUpperCase() }))} className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-sm font-mono font-bold focus:outline-none focus:border-clinica-primary" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="dest-nombre" className="block mb-1 text-[11px] font-bold text-slate-500">Nombre o razón social</label>
                                    <input id="dest-nombre" type="text" value={destinatario.nombre} onChange={(e) => setDestinatario(d => ({ ...d, nombre: e.target.value }))} className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:border-clinica-primary" />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="dest-domicilio" className="block mb-1 text-[11px] font-bold text-slate-500">Domicilio (opcional)</label>
                                <input id="dest-domicilio" type="text" value={destinatario.domicilio} onChange={(e) => setDestinatario(d => ({ ...d, domicilio: e.target.value }))} className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:border-clinica-primary" />
                            </div>
                        </div>
                    )}

                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide">Conceptos a Cobrar</h3>
                            <div className="flex items-center gap-2">
                            {servicios.some(s => s.activo && s.precio > 0) && (
                                <select aria-label="Añadir servicio del tarifario" value="" onChange={(e) => anadirDelTarifario(e.target.value)}
                                        className="py-1.5 px-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-clinica-primary">
                                    <option value="">+ Del tarifario…</option>
                                    {servicios.filter(s => s.activo && s.precio > 0).map(s => (
                                        <option key={s.codigo} value={s.codigo}>{s.nombre} · {euros(Number(s.precio))}</option>
                                    ))}
                                </select>
                            )}
                            <button onClick={handleAgregarConcepto} className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-clinica-primary bg-clinica-primary/10 hover:bg-clinica-primary hover:text-white rounded-lg transition-colors focus:outline-none">
                                <AddIcon fontSize="small" /> Añadir Línea
                            </button>
                            </div>
                        </div>

                        <div className="space-y-3 max-h-[180px] overflow-y-auto pr-1">
                            {conceptos.map((item, index) => (
                                <div key={index} className="flex items-start sm:items-center gap-2 sm:gap-3 flex-col sm:flex-row">
                                    <input
                                        type="text"
                                        placeholder="Descripción del servicio"
                                        value={item.descripcion}
                                        onChange={(e) => handleConceptoChange(index, 'descripcion', e.target.value)}
                                        className="flex-1 w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:border-clinica-primary transition-colors"
                                        required
                                    />
                                    <div className="flex items-center gap-2 w-full sm:w-auto">
                                        <div className="relative flex-1 sm:w-32">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="0.00"
                                                value={item.precio || ''}
                                                onChange={(e) => handleConceptoChange(index, 'precio', e.target.value)}
                                                className="w-full py-2 pl-3 pr-8 bg-white border border-slate-200 rounded-lg text-sm font-bold text-right focus:outline-none focus:border-clinica-primary transition-colors"
                                                required
                                            />
                                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm font-bold text-slate-400 pointer-events-none">€</span>
                                        </div>
                                        <select aria-label="IVA de la línea" value={item.iva || 0} onChange={(e) => handleConceptoChange(index, 'iva', e.target.value)}
                                                className="py-2 px-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 focus:outline-none focus:border-clinica-primary">
                                            {[0, 4, 10, 21].map(v => <option key={v} value={v}>{v === 0 ? 'Exento' : `IVA ${v} %`}</option>)}
                                        </select>
                                        <button onClick={() => handleEliminarConcepto(index)} disabled={conceptos.length === 1} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:hover:bg-transparent focus:outline-none flex-shrink-0">
                                            <DeleteIcon fontSize="small" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-end">
                        <div role="group" aria-label="Método de pago">
                            <div className="block mb-2 text-xs font-bold text-slate-500 uppercase tracking-wide">Método de Pago</div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setMetodoPago('tarjeta')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-bold transition-all focus:outline-none ${metodoPago === 'tarjeta' ? 'bg-sky-100 text-sky-700 border-2 border-sky-300 shadow-sm' : 'bg-white text-slate-500 border-2 border-slate-200 hover:border-sky-200'}`}
                                >
                                    <CardIcon fontSize="small" /> TPV
                                </button>
                                <button
                                    onClick={() => setMetodoPago('efectivo')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-bold transition-all focus:outline-none ${metodoPago === 'efectivo' ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300 shadow-sm' : 'bg-white text-slate-500 border-2 border-slate-200 hover:border-emerald-200'}`}
                                >
                                    <CashIcon fontSize="small" /> Efectivo
                                </button>
                                <button
                                    onClick={() => setMetodoPago('transferencia')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-bold transition-all focus:outline-none ${metodoPago === 'transferencia' ? 'bg-violet-100 text-violet-700 border-2 border-violet-300 shadow-sm' : 'bg-white text-slate-500 border-2 border-slate-200 hover:border-violet-200'}`}
                                >
                                    <BankIcon fontSize="small" /> Transf.
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col items-start sm:items-end p-4 bg-slate-900 rounded-2xl shadow-inner">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Liquidación</span>
                            <span className="text-3xl font-black text-white tracking-tight">{euros(totalFactura)}</span>
                            {ivaIncluido > 0 && <span className="text-[11px] font-bold text-slate-400 mt-1">IVA incluido: {euros(ivaIncluido)}</span>}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="factura-observaciones" className="block mb-1.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Observaciones (Opcional)</label>
                        <textarea id="factura-observaciones" rows="2" placeholder="Notas contables internas..." value={observaciones} onChange={(e) => setObservaciones(e.target.value)} className="w-full py-2.5 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-sm font-medium focus:outline-none focus:border-clinica-primary focus:ring-1 focus:ring-clinica-primary transition-all resize-none" />
                    </div>
                </div>

                <div className="p-4 bg-white border-t border-slate-100 space-y-3">
                    {errorMsg && (
                        <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm font-semibold text-rose-700" role="alert">
                            {errorMsg}
                        </div>
                    )}
                    <button onClick={handleSubmit} disabled={loading || propuesta?.yaFacturada} className="w-full flex items-center justify-center gap-2 py-4 text-sm font-black text-white uppercase tracking-wider bg-clinica-primary hover:bg-blue-700 rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-clinica-primary/30 focus:outline-none">
                        {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : 'Confirmar Asiento y Cobrar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModalNuevaFactura;